import {
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { isMobile } from "react-device-detect";

const useStylesBootstrap = makeStyles((theme) => ({
  arrow: {
    color: "#f5f5f9",
  },
  tooltip: {
    backgroundColor: theme.palette.common.black,
    whiteSpace: "pre-line",
   "@media screen and (min-width: 768px) and (max-width: 900px)": {
      maxWidth: 190,
    },
  },
}));

interface FilterToolTipProps {
  children: ReactNode;
  ToolTipText: string;
}

let globalTooltipClear: (() => void) | null = null;

function FilterToolTip({
  children, 
  ToolTipText,
}: FilterToolTipProps) {
  const [openTooltip, setOpenTooltip] = useState(false);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const classes = useStylesBootstrap();

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const FacilitySpecName = textRef.current;
        const isOverflowing =
          FacilitySpecName.scrollHeight > FacilitySpecName.clientHeight;
        setShouldShowTooltip(isOverflowing);
      }
    };

    const timeout = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timeout);
  }, [ToolTipText]);

  const clearExistingTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpenTooltip(false);
  };

  const handleTooltip = <T extends KeyboardEvent | MouseEvent>(event: T) => {
    if (!isMobile) return;
    event.stopPropagation();

    if (globalTooltipClear) globalTooltipClear();

    setOpenTooltip(true);
    globalTooltipClear = clearExistingTooltip;

    timeoutRef.current = setTimeout(() => {
      setOpenTooltip(false);
      if (globalTooltipClear === clearExistingTooltip) {
        globalTooltipClear = null;
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Tooltip
      arrow
      placement="top"
      classes={classes}
      title={shouldShowTooltip ? ToolTipText : ""}
      open={openTooltip && shouldShowTooltip}
    >
      <span
        ref={textRef}
        role="button"
        tabIndex={0}
        onClick={(e) => handleTooltip(e)}
        onKeyPress={(e) => handleTooltip(e)}
        onMouseOver={() => !isMobile && setOpenTooltip(true)}
        onFocus={() => !isMobile && setOpenTooltip(true)}
        onMouseOut={() => !isMobile && setOpenTooltip(false)}
        onBlur={() => !isMobile && setOpenTooltip(false)}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          cursor: "pointer",
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
}

export default FilterToolTip;
